import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AlertCircle } from "lucide-react";
import {
  ANALYSIS_TYPES,
  getFieldsConfigByAnalysisType,
  cleanContextByAnalysisType,
  validateAnalysisContext,
} from "../utils/analysisContextUtils";
import "../styles/AnalysisContextPanel.css";

import BranchSelector from "./AnalysisContext/BranchSelector";
import DeviceSelector from "./AnalysisContext/DeviceSelector";
import MetricSelector from "./AnalysisContext/MetricSelector";
import CalculatedParameterSelector from "./AnalysisContext/CalculatedParameterSelector";
import PeriodSelector from "./AnalysisContext/PeriodSelector";

const AnalysisContextPanel = ({ onContextChange, onGenerateReport }) => {
  const dispatch = useDispatch();
  const userToken = useSelector((state) => state.user.currentUser?.accessToken);

  const [context, setContext] = useState({
    analysisType: "",
    branch: null,
    device: null,
    metric: null,
    calculatedParameter: null,
    period: {
      mode: "month",
      month: "",
      startDate: "",
      endDate: "",
    },
  });

  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidation, setShowValidation] = useState(false);

  // Obtener configuración de campos según el tipo de análisis
  const fieldsConfig = getFieldsConfigByAnalysisType(context.analysisType);

  /**
   * Maneja el cambio de tipo de análisis
   */
  const handleAnalysisTypeChange = useCallback((newType) => {
    setContext((prev) => {
      const cleaned = cleanContextByAnalysisType(prev, newType);
      return { ...cleaned, analysisType: newType };
    });
    setShowValidation(false);
    setValidationErrors([]);
  }, []);

  /**
   * Maneja cambios genéricos de contexto
   */
  const handleContextChange = useCallback((field, value) => {
    setContext((prev) => {
      const updated = { ...prev, [field]: value };

      // Si se cambia la rama, limpiar dispositivo y parámetro calculado
      if (field === "branch") {
        updated.device = null;
        updated.calculatedParameter = null;
      }

      // Si se cambia el dispositivo, puede afectar parámetros
      if (field === "device") {
        // El parámetro se recargará en el componente DeviceSelector
        updated.calculatedParameter = null;
      }

      return updated;
    });
  }, []);

  /**
   * Valida y notifica cambios
   */
  useEffect(() => {
    // Limpiar validación al cambiar contexto (sin mostrar errores hasta que el usuario intente enviar)
    if (showValidation) {
      const { errors } = validateAnalysisContext(context);
      setValidationErrors(errors);
    }

    // Notificar al padre
    if (onContextChange) {
      onContextChange(context);
    }
  }, [context, showValidation, onContextChange]);

  /**
   * Valida antes de enviar al chat
   */
  const handleValidate = useCallback(() => {
    const { isValid, errors } = validateAnalysisContext(context);
    setValidationErrors(errors);
    setShowValidation(true);
    return isValid;
  }, [context]);

  const handleGenerateClick = useCallback(() => {
    if (handleValidate() && typeof onGenerateReport === "function") {
      onGenerateReport();
    }
  }, [handleValidate, onGenerateReport]);

  // Exponer validación al padre mediante ref/callback
  React.useEffect(() => {
    if (onContextChange.validateContext) {
      onContextChange.validateContext = handleValidate;
    }
  }, [handleValidate, onContextChange]);

  return (
    <div className="analysis-context-panel">
      <div className="context-panel-header">
        <h3 className="context-panel-title">Contexto del análisis</h3>
        {validationErrors.length > 0 && (
          <div className="context-validation-indicator">
            <AlertCircle size={16} />
            <span>{validationErrors.length}</span>
          </div>
        )}
      </div>

      <div className="context-fields">
        {/* Tipo de Análisis - Siempre visible */}
        <div className="context-field">
          <label className="field-label">Tipo de análisis *</label>
          <select
            className="field-select"
            value={context.analysisType}
            onChange={(e) => handleAnalysisTypeChange(e.target.value)}
          >
            <option value="">Seleccionar tipo de análisis...</option>
            {ANALYSIS_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {context.analysisType && (
            <div className="field-description">{fieldsConfig.description}</div>
          )}
        </div>

        {/* Rama / Sucursal */}
        {fieldsConfig.showBranch && (
          <div className="context-field">
            <label className="field-label">
              Planta / sucursal
              {context.analysisType !== "costs" ? "" : " *"}
            </label>
            <BranchSelector
              value={context.branch}
              onChange={(value) => handleContextChange("branch", value)}
              userToken={userToken}
              dispatch={dispatch}
            />
          </div>
        )}

        {/* Dispositivo / Medidor */}
        {fieldsConfig.showDevice && (
          <div className="context-field">
            <label className="field-label">Dispositivo / medidor</label>
            <DeviceSelector
              value={context.device}
              onChange={(value) => handleContextChange("device", value)}
              branchId={context.branch?.id}
              userToken={userToken}
              dispatch={dispatch}
            />
          </div>
        )}

        {/* Métrica Básica (para Consumo) */}
        {fieldsConfig.showMetric && (
          <div className="context-field">
            <label className="field-label">Parámetro / métrica *</label>
            <MetricSelector
              value={context.metric}
              onChange={(value) => handleContextChange("metric", value)}
              deviceId={context.device?.id}
              userToken={userToken}
              dispatch={dispatch}
            />
          </div>
        )}

        {/* Parámetro Calculado */}
        {fieldsConfig.showCalculatedParameter && (
          <div className="context-field">
            <label className="field-label">Parámetro calculado *</label>
            <CalculatedParameterSelector
              value={context.calculatedParameter}
              onChange={(value) =>
                handleContextChange("calculatedParameter", value)
              }
              deviceId={context.device?.id}
              branchId={context.branch?.id}
              userToken={userToken}
              dispatch={dispatch}
            />
          </div>
        )}

        {/* Período */}
        {context.analysisType && (
          <div className="context-field">
            <label className="field-label">
              Período *
            </label>
            <PeriodSelector
              value={context.period}
              onChange={(value) => handleContextChange("period", value)}
              allowCustomRange={
                fieldsConfig.periodMode === "flexible"
              }
            />
          </div>
        )}
      </div>

      <div className="context-panel-actions">
        <button
          type="button"
          className="generate-report-button"
          onClick={handleGenerateClick}
          disabled={!context.analysisType}
        >
          Generar informe
        </button>
      </div>

      {/* Errores de validación */}
      {showValidation && validationErrors.length > 0 && (
        <div className="context-errors">
          <div className="errors-title">Errores a corregir:</div>
          <ul className="errors-list">
            {validationErrors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AnalysisContextPanel;
